import type {Extension, CreateExtArgs, ExtensionAPI, DalItem, DmApis} from '@wix/document-manager-core'
import type {
    CompRef,
    ContextListItem,
    ContextsObject,
    ListContextsOptions,
    BoundObjectRef,
    Pointer,
    ContextConfiguration
} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {DataExtensionAPI} from '../data'
import type {HooksExtensionApi} from '../hooks/hooks'
import {DATA_TYPES} from '../../constants/constants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import {CONTEXTS_HOOKS} from './hooks'
import _ from 'lodash'

type ContextsExtensionAPI = ExtensionAPI & {
    contexts: ContextsObject
}

export type ContextsExtensionAPIInternal = ExtensionAPI & {
    contexts: ContextsObject & {
        getContextPointer(boundRef: BoundObjectRef): Pointer
    }
}

const throwContextsError = (method: string, message: string): never => {
    throw new ReportableError({errorType: 'ContextsError', message: `contexts.${method}: ${message}`})
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, pointers}: CreateExtArgs): ContextsExtensionAPIInternal => {
        const {
            dataModel,
            data: dataExtension,
            hooks
        } = extensionAPI as DataModelExtensionAPI & DataExtensionAPI & HooksExtensionApi
        const validateCompRef = (method: string, compRef: CompRef): void => {
            if (!compRef) {
                throwContextsError(
                    method,
                    'componentPointer is required. Pass the pointer of the component you want to attach the context to'
                )
            }
        }

        const validateContextType = (method: string, contextType: string): void => {
            if (!contextType) {
                throwContextsError(
                    method,
                    'contextType is required. Pass the name of the context you want to attach to the component'
                )
            }
            if (!_.isString(contextType)) {
                throwContextsError(
                    method,
                    'contextType must be a string. Pass the name of the context you want to attach to the component'
                )
            }
        }

        const validateData = (method: string, data: object): void => {
            if (!data) {
                throwContextsError(
                    method,
                    'data is required. You must pass a data object. If you want to remove all data, use contexts.data.remove'
                )
            }
            if (!_.isObject(data)) {
                throwContextsError(
                    method,
                    'data must be an object. You must pass a data object. If you want to remove all data, use contexts.data.remove'
                )
            }
        }

        const validateProperties = (method: string, properties: string[]): void => {
            if (!properties) {
                throwContextsError(method, 'properties is required.')
            }
            if (!_.isArray(properties)) {
                throwContextsError(method, 'properties must be an array')
            }
        }

        const validateComponentExists = (method: string, {id, type}: CompRef): void => {
            if (!dataModel.getItem(id, type)) {
                throwContextsError(
                    method,
                    `Component not found: ${id}. Pass a pointer of a component that exists in the document`
                )
            }
        }

        const getContextListFromCompRef = (compRef: CompRef): DalItem | undefined => {
            return dataModel.components.getItem(compRef, DATA_TYPES.contexts)
        }

        const getContextConfigFromCompRef = (
            compRef: CompRef,
            contextType: string,
            optionalContextList?: DalItem
        ): DalItem | undefined => {
            const contextList = optionalContextList ?? getContextListFromCompRef(compRef)
            return contextList?.values.find((c: DalItem) => c.contextType === contextType)
        }

        const attachContext = (compRef: CompRef, contextType: string, data?: object): void => {
            validateCompRef('attach', compRef)
            validateContextType('attach', contextType)
            validateComponentExists('attach', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList) {
                dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                    type: 'ContextsList',
                    values: [
                        {
                            type: 'ContextConfig',
                            contextType,
                            referredType: contextType,
                            data: !_.isEmpty(data) ? data : undefined
                        }
                    ]
                })
                return
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (existingContext) {
                return
            }

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: [
                    ...contextList.values,
                    {
                        type: 'ContextConfig',
                        referredType: contextType,
                        contextType,
                        data: !_.isEmpty(data) ? data : undefined
                    }
                ]
            })
        }

        const detachContext = (compRef: CompRef, contextType: string): void => {
            validateCompRef('detach', compRef)
            validateContextType('detach', contextType)
            validateComponentExists('detach', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                return
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                return
            }

            const remainingContexts = contextList.values.filter((c: DalItem) => c.contextType !== contextType)
            if (_.isEmpty(remainingContexts)) {
                dataModel.components.removeItem(compRef, DATA_TYPES.contexts)
            } else {
                dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                    type: 'ContextsList',
                    values: remainingContexts
                })
            }

            hooks.executeHook(CONTEXTS_HOOKS.DETACH_AFTER.createEvent({compRef, contextType}))
        }

        const getContextListItemsFromContextList = (
            contextList: DalItem,
            target: ContextListItem['target']
        ): ContextListItem[] => {
            return contextList.map((context: DalItem) => ({
                contextType: context.contextType,
                target
            }))
        }

        // TODO: This function is temporary, to provide backward compatibility
        const normalizeBoundObjectRef = (boundRef: CompRef | BoundObjectRef): BoundObjectRef => {
            if ((boundRef as any).compRef) {
                return boundRef as BoundObjectRef
            }
            return {compRef: boundRef as CompRef}
        }

        const listContexts = (boundRef: CompRef | BoundObjectRef, options?: ListContextsOptions): ContextListItem[] => {
            validateCompRef('list', boundRef as CompRef)
            boundRef = normalizeBoundObjectRef(boundRef)

            const {compRef, contextType} = boundRef

            validateComponentExists('list', compRef)

            const allContexts: ContextListItem[] = []

            const selfContextList = getContextListFromCompRef(compRef)
            if (selfContextList?.values?.length) {
                let contexts = selfContextList.values
                if (contextType) {
                    const contextTypeIndex = contexts.findIndex((c: DalItem) => c.contextType === contextType)
                    if (contextTypeIndex >= 0) {
                        contexts = contexts.slice(0, contextTypeIndex)
                    }
                }
                allContexts.push(...getContextListItemsFromContextList(contexts, 'self'))
            }

            if (options?.includeAncestors) {
                const ancestors = pointers.structure.getAncestors(compRef)
                for (const ancestor of ancestors) {
                    const ancestorContextList = getContextListFromCompRef(ancestor as CompRef)
                    if (ancestorContextList?.values?.length) {
                        allContexts.push(...getContextListItemsFromContextList(ancestorContextList.values, 'above'))
                    }
                }
            }

            const byContextType = new Map<string, ContextListItem>()
            for (const item of allContexts) {
                if (item.target === 'self' || !byContextType.has(item.contextType)) {
                    byContextType.set(item.contextType, item)
                }
            }
            return Array.from(byContextType.values())
        }

        const getData = (compRef: CompRef, contextType: string): ContextConfiguration | undefined => {
            validateCompRef('data.get', compRef)
            validateContextType('data.get', contextType)
            validateComponentExists('data.get', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError(
                    'data.get',
                    `No context type ${contextType} for component: ${compRef.id}, a context must exist on this component for you to get it's data`
                )
            }

            const context = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!context) {
                throwContextsError('data.get', `Context not found: ${contextType}`)
            }

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(context?.data?._id)

            return dataExtension.getInLanguage(pageId, dataId) as ContextConfiguration | undefined
        }

        const updateData = (compRef: CompRef, contextType: string, data: ContextConfiguration): void => {
            validateCompRef('data.update', compRef)
            validateContextType('data.update', contextType)
            validateData('data.update', data)
            validateComponentExists('data.update', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError(
                    'data.update',
                    `No context type ${contextType} for component: ${compRef.id}, a context must exist on this component for you to update it's data`
                )
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                throwContextsError(
                    'data.update',
                    `Context not found: ${contextType}. A context must exist on this component for you to update it's data`
                )
            }

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: contextList?.values.map((c: DalItem) => (c.contextType === contextType ? {...c, data} : c))
            })
        }

        const removeData = (compRef: CompRef, contextType: string): void => {
            validateCompRef('data.remove', compRef)
            validateContextType('data.remove', contextType)
            validateComponentExists('data.remove', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError('data.remove', `No context for component: ${compRef.id}`)
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                throwContextsError('data.remove', `Context not found: ${contextType}`)
            }

            const pageId = pointers.structure.getPageOfComponent(compRef).id
            const dataId = stripHashIfExists(existingContext?.data?._id)

            if (!dataId) {
                return
            }
            dataModel.addItem({...existingContext, data: null}, DATA_TYPES.contexts, pageId, existingContext?.id)
            const dataPointer = pointers.data.getItem(DATA_TYPES.data, dataId, pageId)
            dataModel.removeItemRecursively(dataPointer)
        }

        const removeDataProperties = (compRef: CompRef, contextType: string, properties: string[]): void => {
            validateCompRef('data.removeProperties', compRef)
            validateContextType('data.removeProperties', contextType)
            validateProperties('data.removeProperties', properties)
            validateComponentExists('data.removeProperties', compRef)

            const contextList = getContextListFromCompRef(compRef)
            if (!contextList?.values?.length) {
                throwContextsError('data.removeProperties', `No context for component: ${compRef.id}`)
            }

            const existingContext = getContextConfigFromCompRef(compRef, contextType, contextList)
            if (!existingContext) {
                throwContextsError('data.removeProperties', `Context not found: ${contextType}`)
            }

            const nullifiedProperties = Object.fromEntries(properties.map(prop => [prop, null]))

            dataModel.components.addItem(compRef, DATA_TYPES.contexts, {
                type: 'ContextsList',
                values: contextList!.values.map((c: DalItem) =>
                    c.contextType === contextType ? {...c, data: nullifiedProperties} : c
                )
            })
        }

        const getContextPointer = (boundRef: BoundObjectRef): Pointer => {
            const {compRef, contextType} = boundRef
            validateContextType('getContextPointer', contextType!)
            validateCompRef('getContextPointer', compRef)
            const contextConfig = getContextConfigFromCompRef(compRef, contextType!)
            if (!contextConfig) {
                throwContextsError(
                    'getContextPointer',
                    `Context not found: ${contextType}. A context must exist on this component to get its pointer`
                )
            }
            return pointers.getPointer(contextConfig!.id!, DATA_TYPES.contexts)
        }

        return {
            contexts: {
                attach: attachContext,
                detach: detachContext,
                list: listContexts,
                getContextPointer,
                data: {
                    get: getData,
                    update: updateData,
                    remove: removeData,
                    removeProperties: removeDataProperties
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): ContextsExtensionAPI => {
        const {contexts} = extensionAPI as ContextsExtensionAPIInternal
        return {
            contexts: {
                attach: contexts.attach,
                detach: contexts.detach,
                list: contexts.list,
                data: {
                    get: contexts.data.get,
                    update: contexts.data.update,
                    remove: contexts.data.remove,
                    removeProperties: contexts.data.removeProperties
                }
            }
        }
    }

    return {
        name: 'contexts',
        dependencies: new Set(['dataModel', 'structure', 'hooks']),
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
